import "./assignProjectList.css"; // reused CSS for exact styling
import React, { useEffect, useState } from "react";
import SearchBar from "../Main/searchBar";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as BackIcon } from "../../Assets/Categories/BackIcon.svg";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  getProjectList,
  revokeBuilderProjectAssignment,
} from "../../Redux/Actions/franchiseAction";
import { Pagination, Dropdown, Menu, Button } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { trimKey } from "../../Utils/helpers";

const AssignProjectList = () => {
  const { selectedFranchiseId: franchiseId } = useSelector(
    (state) => state.franchises
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [projectList, setProjectList] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  const [selectedAcls, setSelectedAcls] = useState(null);
  const [isAclsPopupOpen, setIsAclsPopupOpen] = useState(false);

  useEffect(() => {
    if (franchiseId) {
      fetchProjectList(pagination.current, pagination.pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [franchiseId]);

  const fetchProjectList = async (page, pageSize) => {
    setIsLoadingProjects(true);
    try {
      const payload = {
        franchise_id: franchiseId,
        status: ["approved", "published", "unpublished", "sold", "requested"],
        is_deleted: false,
        page,
        limit: pageSize,
      };
      const res = await dispatch(getProjectList(payload));
      if (res && res.data && Array.isArray(res.data)) {
        setProjectList(res.data);
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: res.total ?? 0,
        }));
        if (res.data.length === 0)
          toast("No projects found for this franchise.");
      } else {
        setProjectList([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        toast("No projects found for this franchise.");
      }
    } catch {
      toast.error("Failed to fetch projects");
      setProjectList([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const handleRevokeProjectClick = async (project) => {
    if (!franchiseId || !project._id || !project.builder) {
      toast.error("Invalid franchise, project, or builder ID");
      return;
    }
    const payload = {
      franchise_id: franchiseId,
      project_id: project._id,
      builder_id: project.builder,
    };
    try {
      const res = await dispatch(revokeBuilderProjectAssignment(payload));
      if (res && res.statusCode === 200) {
        toast.success("Project revoked successfully");
        fetchProjectList(pagination.current, pagination.pageSize);
      } else {
        toast.error(res.message || "Failed to revoke project");
      }
    } catch {
      toast.error("Failed to revoke project");
    }
  };

  const openAclsPopup = (acls) => {
    if (acls && acls.length > 0) {
      setSelectedAcls(acls);
      setIsAclsPopupOpen(true);
    }
  };

  const handleProjectCatalogueClick = (project) => {
    const projectTypeId = project.project_type?._id || "";
    const projectTypeName = trimKey(
      project.project_type?.name_withoutspace || ""
    );
    const projectId = project._id || "";
    const categories = project.categories || [];

    // Pick relevant category id for route based on conditions
    let categoryId = "";

    if (projectTypeName === "residential") {
      if (categories.some((cat) => trimKey(cat.name_withoutspace) === "plot")) {
        categoryId = categories.find(
          (cat) => trimKey(cat.name_withoutspace) === "plot"
        )._id;
        navigate(
          `/franchise/plot_catalogue_list/${categoryId}/${projectTypeId}/${projectId}`
        );
        return;
      }
      if (
        categories.some((cat) => trimKey(cat.name_withoutspace) === "apartment")
      ) {
        categoryId = categories.find(
          (cat) => trimKey(cat.name_withoutspace) === "apartment"
        )._id;
        navigate(
          `/franchise/residential_catalogue_list/${categoryId}/${projectTypeId}/${projectId}`
        );
        return;
      }
      toast.error("Unsupported residential category");
    } else if (projectTypeName === "commercial") {
      if (
        categories.some((cat) => {
          const name = trimKey(cat.name_withoutspace);
          return name === "industrialplot" || name === "commercialplot";
        })
      ) {
        categoryId = categories.find((cat) => {
          const name = trimKey(cat.name_withoutspace);
          return name === "industrialplot" || name === "commercialplot";
        })._id;
        navigate(
          `/franchise/plot_catalogue_list/${categoryId}/${projectTypeId}/${projectId}`
        );
        return;
      }
      // Default commercial catalogue
      categoryId = categories[0]?._id || "";
      navigate(
        `/franchise/commercial_catalogue_list/${categoryId}/${projectTypeId}/${projectId}`
      );
    } else {
      toast.error("Unsupported project type");
    }
  };

  const closeAclsPopup = () => {
    setIsAclsPopupOpen(false);
    setSelectedAcls(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No Data";
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  function toTitleCase(str) {
    return str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }

  return (
    <div className="grid-container">
      <div
        className="padding-20"
        style={{ gridColumn: "3/8", marginBottom: 20 }}
      >
        <SearchBar style={{ width: "max-content" }} />
      </div>

      <div
        style={{
          gridColumn: "span 7",
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingRight: 16,
        }}
      >
        {/* Left group: Back icon + title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexShrink: 0,
          }}
        >
          <Link to={`/franchise/assign-builder-list/${franchiseId}`} state={{ franchiseId }}>
            <BackIcon style={{ cursor: "pointer", width: 80, height: 80 }} />
          </Link>
          <h3 style={{ margin: 0, fontWeight: 600, color: "#181E2E" }}>
            Projects Assigned
          </h3>
        </div>

        {/* Right side button */}
        <Link
          to="/franchise/projects_list"
          style={{
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
            height: 66,
            width: 200,
            backgroundColor: "#181E2E",
            color: "#fff",
            textDecoration: "none",
            fontWeight: "bold",
            borderRadius: 32,
            cursor: "pointer",
          }}
          className="assign-builder-link"
        >
          Projects List
        </Link>
      </div>

      <div className="overall-container">
        <div
          className={`builder-list-wrapper ${
            projectList.length === 0 && !isLoadingProjects ? "centered" : ""
          }`}
        >
          {isLoadingProjects ? (
            <div className="loader">Loading projects...</div>
          ) : projectList.length > 0 ? (
            <>
              <table className="builder-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Project ID</th>
                    <th>Project Name</th>
                    <th>Brand Name</th>
                    <th>ACLs</th>
                    <th>Created At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {projectList.map((project, index) => (
                    <tr key={project._id}>
                      <td>
                        {(pagination.current - 1) * pagination.pageSize +
                          index +
                          1}
                      </td>
                      <td>{project.project_id ?? "No Data"}</td>
                      <td>{project.project_name ?? "No Data"}</td>
                      <td>{project.brand_name ?? "No Data"}</td>
                      <td>
                        {project.acls && project.acls.length > 0 ? (
                          <span
                            onClick={() =>
                              openAclsPopup(
                                project.acls.map((acl) => ({
                                  ...acl,
                                  name: toTitleCase(acl.name),
                                  access: acl.access.map(toTitleCase),
                                }))
                              )
                            }
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color: "white",
                            }}
                          >
                            View ACLs
                          </span>
                        ) : (
                          "No ACLs"
                        )}
                      </td>

                      <td>{formatDate(project.created_at)}</td>
                      <td>
                        <Dropdown
                          overlay={
                            <Menu>
                              <Menu.Item
                                key="revoke"
                                onClick={() =>
                                  handleRevokeProjectClick(project)
                                }
                              >
                                Revoke
                              </Menu.Item>
                              <Menu.Item
                                key="project_catalogue_list"
                                onClick={() =>
                                  handleProjectCatalogueClick(project)
                                }
                              >
                                Project Catalogue List
                              </Menu.Item>

                              <Menu.Item key="Assign Projects Catalogue">
                                <Link
                                  to={`/franchise/assign_projects_catalogue`}
                                >
                                  Assigned Project Catalogue
                                </Link>
                              </Menu.Item>
                            </Menu>
                          }
                          trigger={["click"]}
                        >
                          <Button shape="square" icon={<EllipsisOutlined />} />
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                pageSizeOptions={["5", "10", "15", "20"]}
                onChange={(page, pageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize,
                  }));
                  fetchProjectList(page, pageSize);
                }}
                style={{ marginTop: 20, textAlign: "center" }}
              />
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                fontSize: 18,
                color: "rgba(255,255,255,0.7)",
                padding: 30,
              }}
            >
              No projects found for this franchise.
            </div>
          )}
        </div>
      </div>

      {isAclsPopupOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={closeAclsPopup}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: 20,
              borderRadius: 8,
              width: 300,
              maxHeight: "80vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4>ACL List</h4>
            <ul>
              {selectedAcls.map((acl) => (
                <li key={acl._id}>
                  <strong>{acl.name}</strong>: {acl.access.join(", ")}
                </li>
              ))}
            </ul>
            <button onClick={closeAclsPopup} style={{ marginTop: 10 }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignProjectList;
